import { format, subDays, subMonths } from "date-fns";

export type SalesProductTabId =
  | "overview"
  | "sales"
  | "inventory"
  | "customers"
  | "attribution"
  | "financials";

export interface SalesProductTabItem {
  id: SalesProductTabId;
  label: string;
}

export interface SalesOrder {
  id: string;
  orderName: string;
  customerFullName: string;
  totalPrice: number;
  /** Pre-formatted amount from the API (e.g. "USD 4,943"), preferred over formatting `totalPrice` locally. */
  totalPriceFormattedValue?: string;
  orderStatus: string;
  /** Localized display label for `orderStatus` (e.g. "Unknown" when the raw status is normalized to ""). */
  orderStatusLabel?: string;
  date: string;
  /** Localized "Month Day, Year" from the API, preferred over formatting `date` locally. */
  dateFormattedValue?: string;
}

export interface SalesProduct {
  id: string;
  name: string;
  sku: string;
  category: string;
  price: number;
  /** Pre-formatted amount from the API, preferred over formatting `price` locally. */
  priceFormattedValue?: string;
  cost: number;
  stock: number;
  /** Pre-formatted count from the API, preferred over formatting `stock` locally. */
  stockFormattedValue?: string;
  stockUnavailable?: boolean;
  reorderPoint: number;
  status: "in_stock" | "low_stock" | "out_of_stock";
  statusUnavailable?: boolean;
  imageUrl?: string;
}

export type SalesCustomerAccountStatus = "enabled" | "disabled" | "invited" | (string & {});

export interface SalesCustomer {
  id: string;
  name: string;
  email: string;
  phone: string;
  segments: string[];
  totalOrders: number;
  /** Pre-formatted count from the API, preferred over formatting `totalOrders` locally. */
  totalOrdersFormattedValue?: string;
  totalSpent: number;
  /** Pre-formatted amount from the API (e.g. "USD 2,717"), preferred over formatting `totalSpent` locally. */
  totalSpentFormattedValue?: string;
  avgOrderValue: number;
  /** Most recent order date (YYYY-MM-DD), or the localized "not ordered yet" label when the customer has no orders. */
  recentOrderDate: string;
  /** Localized display counterpart of `recentOrderDate` — same "not ordered yet" label when there's no date to format. */
  recentOrderDateFormattedValue?: string;
  /** Customer sign up date (YYYY-MM-DD), or "" when the DB value itself is null. */
  signUpDate: string;
  /** Localized display counterpart of `signUpDate`. */
  signUpDateFormattedValue?: string;
  /** Shopify account state (e.g. enabled, disabled, invited). */
  status: SalesCustomerAccountStatus;
  cltv: number;
}

export interface TrendPoint {
  date: string;
  revenue: number;
  orders: number;
}

export interface MonthlyPoint {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
}

export interface DistributionPoint {
  label: string;
  value: number;
  sharePercent?: number;
  /** Pre-formatted amount from the API (e.g. "USD 2,006,675"), preferred over formatting `value` locally. */
  formattedValue?: string;
}

export interface BucketPoint {
  label: string;
  value: number;
}

const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const productNames = [
  "Ajwa Dates Premium Box",
  "Black Crepe Abaya",
  "Cambodian Oud Oil",
  "Saudi Arabica Coffee Beans",
  "Majlis Floor Cushion Set",
  "Sukkari Dates 1kg",
  "Embroidered Open Abaya",
  "Bakhoor Maamoul Pack",
  "Saudi Coffee Dallah",
  "Sadu Patterned Rug",
  "Khudri Dates Gift Tray",
  "Linen Summer Abaya",
  "Taif Rose Perfume",
  "Cardamom & Saffron Blend",
  "Arabic Tea Glass Set",
  "Date Maamoul Sweets Box",
  "Butterfly Sleeve Abaya",
  "Oud & Amber Body Mist",
  "Green Cardamom 250g",
  "Incense Burner Mabkhara",
];

const categories = [
  "Dates & Sweets",
  "Abayas & Fashion",
  "Fragrance & Oud",
  "Coffee & Tea",
  "Home & Majlis",
];

const customerNames = [
  "Emma Wilson",
  "James Chen",
  "Sofia Rodriguez",
  "Liam O'Brien",
  "Ava Patel",
  "Noah Kim",
  "Isabella Santos",
  "Oliver Martinez",
  "Mia Thompson",
  "Ethan Davis",
  "Charlotte Brown",
  "Lucas Garcia",
  "Amelia Lee",
  "Mason Taylor",
  "Harper Anderson",
  "Aiden Moore",
  "Evelyn White",
  "Elijah Harris",
  "Abigail Clark",
  "Logan Lewis",
  "Emily Robinson",
  "Alexander Walker",
  "Scarlett Hall",
  "Sebastian Young",
  "Victoria King",
];

export const SALES_PRODUCT_TAB_ITEMS: SalesProductTabItem[] = [
  { id: "overview", label: "Overview" },
  { id: "sales", label: "Sales" },
  { id: "inventory", label: "Inventory" },
  { id: "customers", label: "Customers" },
  { id: "attribution", label: "Sales Attribution" },
  // { id: "financials", label: "Financials" },
];

export const generateSalesProductOrders = (): SalesOrder[] => {
  const orders: SalesOrder[] = [];
  const statuses = ["fulfilled", "fulfilled", "fulfilled", "unfulfilled", "partial", "cancelled"];

  for (let i = 0; i < 160; i += 1) {
    const seed = i * 13 + 7;
    const customerIdx = Math.floor(seededRandom(seed) * customerNames.length);
    const date = format(
      subDays(new Date("2026-04-02T00:00:00"), Math.floor(seededRandom(seed + 1) * 360)),
      "yyyy-MM-dd"
    );
    const totalPrice = Math.round((35 + seededRandom(seed + 2) * 540) * 100) / 100;
    orders.push({
      id: `ord-${String(i + 1).padStart(4, "0")}`,
      orderName: `#${21000 + i}`,
      customerFullName: customerNames[customerIdx],
      totalPrice,
      orderStatus: statuses[Math.floor(seededRandom(seed + 4) * statuses.length)],
      date,
    });
  }

  return orders.sort((a, b) => b.date.localeCompare(a.date));
};

export const generateSalesProductProducts = (): SalesProduct[] =>
  productNames.map((name, index) => {
    const seed = index * 7 + 3;
    const price = Math.round((15 + seededRandom(seed) * 85) * 100) / 100;
    const cost = Math.round(
      price * (0.3 + seededRandom(seed + 1) * 0.3) * 100
    ) / 100;
    const stock = Math.floor(seededRandom(seed + 2) * 220);
    const reorderPoint = Math.floor(16 + seededRandom(seed + 3) * 38);

    return {
      id: `prod-${index + 1}`,
      name,
      sku: `SKU-${String(1000 + index)}`,
      category: categories[index % categories.length],
      price,
      cost,
      stock,
      reorderPoint,
      status:
        stock === 0
          ? "out_of_stock"
          : stock < reorderPoint
            ? "low_stock"
            : "in_stock",
    };
  });

export const generateSalesProductCustomers = (): SalesCustomer[] =>
  customerNames.map((name, index) => {
    const seed = index * 17 + 11;
    const totalOrders = 1 + Math.floor(seededRandom(seed) * 24);
    const avgOrderValue = Math.round((30 + seededRandom(seed + 1) * 190) * 100) / 100;
    const totalSpent = Math.round(totalOrders * avgOrderValue * 100) / 100;
    return {
      id: `cust-${index + 1}`,
      name,
      email: `${name.toLowerCase().replace(/[' ]/g, ".")}@example.com`,
      phone: "Not Available",
      segments: [],
      totalOrders,
      totalSpent,
      avgOrderValue,
      recentOrderDate: format(
        subDays(new Date("2026-04-02T00:00:00"), Math.floor(seededRandom(seed + 4) * 30)),
        "yyyy-MM-dd"
      ),
      signUpDate: format(
        subDays(new Date("2026-04-02T00:00:00"), Math.floor(seededRandom(seed + 2) * 75)),
        "yyyy-MM-dd"
      ),
      status: totalOrders > 4 ? "enabled" : "disabled",
      cltv: Math.round(totalSpent * (1 + seededRandom(seed + 3) * 0.55) * 100) / 100,
    };
  });

export const getSalesTrend = (days = 60): TrendPoint[] => {
  const data: TrendPoint[] = [];
  for (let i = days; i >= 0; i -= 1) {
    const seed = i * 19 + 5;
    const weekendFactor =
      [0, 6].includes(subDays(new Date("2026-04-02T00:00:00"), i).getDay()) ? 1.24 : 1;
    const revenue = Math.round((1800 + seededRandom(seed) * 4200) * weekendFactor);
    data.push({
      date: format(subDays(new Date("2026-04-02T00:00:00"), i), "MMM dd"),
      revenue,
      orders: Math.floor(revenue / 95),
    });
  }
  return data;
};

export const getSalesChannelDistribution = (
  _orders: SalesOrder[]
): DistributionPoint[] => [];


export const getMonthlyFinancials = (orders: SalesOrder[]): MonthlyPoint[] => {
  const months: Record<string, MonthlyPoint> = {};
  for (let i = 11; i >= 0; i -= 1) {
    // 30-day steps drift against real month lengths, duplicating some months and
    // skipping others (February first), which drops those orders from the chart.
    const month = format(subMonths(new Date("2026-04-02T00:00:00"), i), "MMM yyyy");
    months[month] = { month, revenue: 0, expenses: 0, profit: 0 };
  }

  orders
    .filter(order => order.orderStatus === "fulfilled")
    .forEach(order => {
      const month = format(new Date(order.date), "MMM yyyy");
      if (!months[month]) {
        return;
      }
      months[month].revenue += order.totalPrice;
      months[month].expenses += order.totalPrice * 0.58;
      months[month].profit += order.totalPrice * 0.42;
    });

  return Object.values(months).map(item => ({
    ...item,
    revenue: Math.round(item.revenue),
    expenses: Math.round(item.expenses),
    profit: Math.round(item.profit),
  }));
};

export const getFinancialSummary = (orders: SalesOrder[]) => {
  const completed = orders.filter(order => order.orderStatus === "fulfilled");
  const revenue = completed.reduce((sum, order) => sum + order.totalPrice, 0);
  const taxes = 0;
  const refunds = 0;
  const cogs = revenue * 0.41;
  const expenses = revenue * 0.17;
  const grossProfit = revenue - cogs;
  const netProfit = revenue - cogs - taxes - expenses - refunds;

  return {
    revenue: Math.round(revenue),
    cogs: Math.round(cogs),
    grossProfit: Math.round(grossProfit),
    taxes,
    expenses: Math.round(expenses),
    refunds,
    netProfit: Math.round(netProfit),
  };
};

export const getOverviewKpis = (
  orders: SalesOrder[],
  customers: SalesCustomer[],
  products: SalesProduct[]
) => {
  const completed = orders.filter(order => order.orderStatus === "fulfilled");
  const totalRevenue = completed.reduce((sum, order) => sum + order.totalPrice, 0);
  const avgOrderValue = completed.length ? totalRevenue / completed.length : 0;
  const inventoryValue = products.reduce(
    (sum, product) => sum + product.stock * product.cost,
    0
  );
  const avgCltv = customers.reduce((sum, customer) => sum + customer.cltv, 0) /
    customers.length;

  return {
    totalRevenue: Math.round(totalRevenue),
    totalOrders: completed.length,
    activeCustomers: customers.length,
    avgOrderValue: Math.round(avgOrderValue),
    inventoryValue: Math.round(inventoryValue),
    avgCltv: Math.round(avgCltv),
  };
};

export const getCltvBuckets = (customers: SalesCustomer[]): BucketPoint[] => [
  { label: "PKR 0-200", value: customers.filter(customer => customer.cltv < 200).length },
  {
    label: "PKR 200-500",
    value: customers.filter(
      customer => customer.cltv >= 200 && customer.cltv < 500
    ).length,
  },
  {
    label: "PKR 500-1k",
    value: customers.filter(
      customer => customer.cltv >= 500 && customer.cltv < 1000
    ).length,
  },
  {
    label: "PKR 1k-2k",
    value: customers.filter(
      customer => customer.cltv >= 1000 && customer.cltv < 2000
    ).length,
  },
  { label: "PKR 2k+", value: customers.filter(customer => customer.cltv >= 2000).length },
];

export const getCustomerSegmentation = (
  customers: SalesCustomer[]
): DistributionPoint[] => [
    {
      label: "New",
      value: customers.filter(customer => customer.status === "enabled").length,
    },
    {
      label: "Returning",
      value: customers.filter(customer => customer.status !== "enabled").length,
    },
  ];

export const getTaxBreakdown = (taxes: number): DistributionPoint[] => [
  { label: "Sales Tax", value: Math.round(taxes * 0.64) },
  { label: "VAT", value: Math.round(taxes * 0.24) },
  { label: "Other", value: Math.round(taxes * 0.12) },
];

export const paginateRows = <T,>(rows: T[], page: number, pageSize = 10) =>
  rows.slice((page - 1) * pageSize, page * pageSize);
